/**
 * InteractivePartViewer - Módulo Profundo de Animação e Inspeção de Bancada (Nível 2B)
 * Domínio: Manutenção Técnica Concessionária Honda ADV 150 (Projeto K0W / Bloco KF43E)
 * Filosofia Fábio Akita: Boring Technology, Deep Modules, Zero Mocks e Fail-Visible.
 */

export type ViewerMode = "exploded" | "assembled" | "step-by-step" | "rpm-simulation";

export interface PartAssemblyStep {
  stepNumber: number;
  partId: string;
  name: string;
  partNumber: string;
  oemSupplier?: string;
  torqueSpec?: string;
  serviceLimitMm?: number;
  nominalDimensionMm?: number;
  criticalGotcha: string;
  requiredTool?: string;
  axialOffsetPx: number;
}

export interface SimulationState {
  rpm: number;
  rollerPositionRatio: number; // 0.0 (repouso) a 1.0 (abertura máxima)
  movableFaceDisplacementMm: number; // 0.0 mm a ~12.0 mm
  driveRatioDescription: string;
}

export const CVT_VARIATOR_STEPS: ReadonlyArray<PartAssemblyStep> = [
  {
    stepNumber: 1,
    partId: "part-01-crankshaft",
    name: "Ponta do Virabrequim (Eixo Motor)",
    partNumber: "13000-K0W-B00",
    oemSupplier: "Honda Dream",
    torqueSpec: "N/A (Eixo Motriz)",
    criticalGotcha:
      "Inspecionar estrias M14 e rosca. Se espanado por montagem incorreta, troca do virabrequim exige abertura total do bloco.",
    requiredTool: "Soquete 17 mm (para girar virabrequim)",
    axialOffsetPx: 0,
  },
  {
    stepNumber: 2,
    partId: "part-02-ramp-plate",
    name: "Placa de Rampa + 3 Cursores Guia (Slide Pieces)",
    partNumber: "22011-K97-T00",
    oemSupplier: "FCC / Honda",
    torqueSpec: "Encaixe deslizante estriado",
    criticalGotcha:
      "Os 3 cursores plásticos não podem ter folga lateral. Folga gera ruído metálico em marcha lenta e desgaste prematuro.",
    requiredTool: "Inspeção manual",
    axialOffsetPx: 50,
  },
  {
    stepNumber: 3,
    partId: "part-03-rollers",
    name: "Jogo de 6 Roletes Centrífugos (21g)",
    partNumber: "22123-K0W-B00",
    oemSupplier: "Bando / Honda",
    torqueSpec: "Alojados nas 6 pistas",
    serviceLimitMm: 19.5,
    nominalDimensionMm: 20.0,
    criticalGotcha:
      "MONTAGEM RIGOROSAMENTE A SECO! NUNCA aplicar graxa nos roletes. A graxa acumula poeira da correia e trava o variador.",
    requiredTool: "Paquímetro digital centesimal (limite: 19,5 mm)",
    axialOffsetPx: 120,
  },
  {
    stepNumber: 4,
    partId: "part-04-movable-face",
    name: "Polia Móvel Dianteira (Movable Drive Face)",
    partNumber: "22110-K0W-B00",
    oemSupplier: "Honda Alumínio Usinado",
    torqueSpec: "Deslizamento axial livre",
    serviceLimitMm: 24.06,
    nominalDimensionMm: 24.0,
    criticalGotcha:
      "Inspecionar face cônica contra ondulações e degraus causados por uso severo. Verificar bucha interna de bronze.",
    requiredTool: "Súbito / Micrômetro interno",
    axialOffsetPx: 180,
  },
  {
    stepNumber: 5,
    partId: "part-05-boss-bushing",
    name: "Bucha Central Deslizante (Drive Face Boss)",
    partNumber: "22105-K97-T00",
    oemSupplier: "Aço Temperado Retificado",
    torqueSpec: "Assenta no batente do eixo",
    serviceLimitMm: 23.96,
    nominalDimensionMm: 23.98,
    criticalGotcha:
      "Diâmetro externo mínimo de 23,960 mm. Aplicar película levíssima de graxa de alta temperatura exclusiva para buchas se previsto no manual de serviço.",
    requiredTool: "Micrômetro externo 25-50 mm",
    axialOffsetPx: 260,
  },
  {
    stepNumber: 6,
    partId: "part-06-drive-belt",
    name: "Correia de Transmissão V-Belt Bando Duplo Denteado",
    partNumber: "23100-K97-T01",
    oemSupplier: "Bando EPDM / Kevlar",
    torqueSpec: "Protocolo Belt Sinking",
    serviceLimitMm: 18.5,
    nominalDimensionMm: 19.5,
    criticalGotcha:
      "GOTCHA FATAL (Protocolo Belt Sinking): A correia DEVE estar frouxa na frente durante o aperto. Se for mordida pela polia fixa, gera falso torque de 59 N.m e destrói o virabrequim.",
    requiredTool: "Paquímetro digital (limite de descarte: 18,5 mm)",
    axialOffsetPx: 330,
  },
  {
    stepNumber: 7,
    partId: "part-07-spacer-washer",
    name: "Arruela Espaçadora Plana de Encosto",
    partNumber: "90401-K97-T00",
    oemSupplier: "Aço estampado",
    torqueSpec: "Assentamento plano",
    criticalGotcha:
      "Garante o espaçamento correto entre a bucha deslizante e as estrias da polia ventilada fixa.",
    requiredTool: "Inspeção visual de paralelismo",
    axialOffsetPx: 380,
  },
  {
    stepNumber: 8,
    partId: "part-08-fixed-face",
    name: "Face Fixa Ventilada com Aletas Radiais (Drive Face)",
    partNumber: "22102-K97-T00",
    oemSupplier: "Honda Alumínio Fundido",
    torqueSpec: "Encaixe estriado direto",
    criticalGotcha:
      "PROIBIDO USO DE TALHADEIRAS! Travar exclusivamente pelos 2 furos reforçados com o Universal Holder Honda 07725-0030000.",
    requiredTool: "Universal Holder 07725-0030000",
    axialOffsetPx: 430,
  },
  {
    stepNumber: 9,
    partId: "part-09-belleville-washer",
    name: "Arruela Cônica Elástica Belleville",
    partNumber: "90441-K97-T00",
    oemSupplier: "Aço mola temperado",
    torqueSpec: "Sob pressão da porca M14",
    criticalGotcha:
      "POSIÇÃO OBRIGATÓRIA: A inscrição gravada 'OUTSIDE' DEVE estar voltada para fora (apoiando sob a face da porca).",
    requiredTool: "Conferência visual de marcação",
    axialOffsetPx: 590,
  },
  {
    stepNumber: 10,
    partId: "part-10-nut-m14",
    name: "Porca Flangeada M14x1,25 (Sextavado 22 mm)",
    partNumber: "90201-GCC-000",
    oemSupplier: "Aço Classe 10.9 Zincado",
    torqueSpec: "59 N.m (6,0 kgf.m)",
    criticalGotcha:
      "TORQUE CRÍTICO: 59 N.m com torquímetro calibrado. Proibido aperto com pistola de impacto sem conferência.",
    requiredTool: "Torquímetro de estalo + Soquete 22 mm (6 pontas)",
    axialOffsetPx: 670,
  },
];

export class InteractivePartViewer {
  private currentMode: ViewerMode = "exploded";
  private currentStepIndex = 0;
  private currentRpm = 1700;
  private readonly steps: ReadonlyArray<PartAssemblyStep>;

  constructor(steps: ReadonlyArray<PartAssemblyStep> = CVT_VARIATOR_STEPS) {
    if (!steps || steps.length === 0) {
      throw new Error("InteractivePartViewer: lista de passos de montagem não pode ser vazia.");
    }
    this.steps = steps;
  }

  public getMode(): ViewerMode {
    return this.currentMode;
  }

  public setMode(mode: ViewerMode): void {
    this.currentMode = mode;
  }

  public getCurrentStep(): PartAssemblyStep {
    const step = this.steps[this.currentStepIndex];
    if (!step) {
      throw new Error(`Passo inválido no índice ${this.currentStepIndex}`);
    }
    return step;
  }

  public getCurrentStepIndex(): number {
    return this.currentStepIndex;
  }

  public getTotalSteps(): number {
    return this.steps.length;
  }

  public nextStep(): PartAssemblyStep {
    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex++;
    }
    return this.getCurrentStep();
  }

  public prevStep(): PartAssemblyStep {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
    }
    return this.getCurrentStep();
  }

  public goToStep(stepNumber: number): PartAssemblyStep {
    if (stepNumber < 1 || stepNumber > this.steps.length) {
      throw new Error(`Número de passo fora dos limites [1, ${this.steps.length}]: ${stepNumber}`);
    }
    this.currentStepIndex = stepNumber - 1;
    return this.getCurrentStep();
  }

  /**
   * Retorna os offsets axiais calculados para cada peça com base no modo ativo
   */
  public getPieceTransforms(): Record<
    string,
    { translateX: number; opacity: number; isHighlighted: boolean }
  > {
    const transforms: Record<
      string,
      { translateX: number; opacity: number; isHighlighted: boolean }
    > = {};
    const activeStep = this.getCurrentStep();

    for (let i = 0; i < this.steps.length; i++) {
      const step = this.steps[i];
      if (!step) continue;

      let translateX = 0;
      let opacity = 1.0;
      let isHighlighted = false;

      switch (this.currentMode) {
        case "assembled":
          translateX = 0;
          opacity = 1.0;
          isHighlighted = false;
          break;

        case "exploded":
          translateX = step.axialOffsetPx;
          opacity = 1.0;
          isHighlighted = false;
          break;

        case "step-by-step":
          translateX = step.axialOffsetPx;
          isHighlighted = step.stepNumber === activeStep.stepNumber;
          // Peças montadas antes permanecem visíveis; peças futuras ficam translúcidas
          opacity = step.stepNumber <= activeStep.stepNumber ? 1.0 : 0.25;
          break;

        case "rpm-simulation": {
          // Deslocamento cinemático proporcional ao RPM
          const sim = this.getSimulationState();
          if (step.partId === "part-04-movable-face") {
            translateX = sim.movableFaceDisplacementMm * 4; // Escala visual em pixels
          } else {
            translateX = 0;
          }
          opacity = 1.0;
          isHighlighted =
            step.partId === "part-03-rollers" || step.partId === "part-04-movable-face";
          break;
        }
      }

      transforms[step.partId] = { translateX, opacity, isHighlighted };
    }

    return transforms;
  }

  /**
   * Simulação cinemática da transmissão CVT e deslocamento centrífugo dos roletes
   */
  public setRpm(rpm: number): SimulationState {
    // Faixa operacional Honda ADV 150: 1.700 RPM (marcha lenta) a 8.500 RPM (potência máxima)
    this.currentRpm = Math.max(1700, Math.min(8500, rpm));
    return this.getSimulationState();
  }

  public getSimulationState(): SimulationState {
    const minRpm = 1700;
    const maxRpm = 8500;
    const ratio = Math.max(0, Math.min(1, (this.currentRpm - minRpm) / (maxRpm - minRpm)));

    // Deslocamento máximo axial da polia móvel ~10.5 mm
    const displacementMm = Number((ratio * 10.5).toFixed(2));

    let description = "Marcha Lenta (1.700 RPM) – Relação Curta 1ª Marcha";
    if (this.currentRpm > 2500 && this.currentRpm < 5500) {
      description = `Aceleração Média (${this.currentRpm} RPM) – Expansão centrífuga dos roletes 21g`;
    } else if (this.currentRpm >= 5500) {
      description = `Velocidade Máxima (${this.currentRpm} RPM) – Overdrive / Relação Longa Final`;
    }

    return {
      rpm: this.currentRpm,
      rollerPositionRatio: Number(ratio.toFixed(3)),
      movableFaceDisplacementMm: displacementMm,
      driveRatioDescription: description,
    };
  }

  /**
   * Renderiza a barra de controles touch de bancada (padrão OLED >= 48dp)
   */
  public renderControlBarHtml(): string {
    const step = this.getCurrentStep();
    return `
<div class="interactive-part-controls" style="background: #0B0F19; border: 1px solid #1E293B; border-radius: 12px; padding: 16px; color: #F8FAFC; font-family: system-ui, sans-serif;">
  <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; justify-content: space-between; align-items: center;">
    <div style="display: flex; gap: 8px;">
      <button type="button" data-action="mode-assembled" style="min-height: 48px; min-width: 48px; padding: 8px 16px; background: ${this.currentMode === "assembled" ? "#0284C7" : "#1E293B"}; color: #FFF; border: 1px solid #334155; border-radius: 8px; font-weight: 700; cursor: pointer;">Montado</button>
      <button type="button" data-action="mode-exploded" style="min-height: 48px; min-width: 48px; padding: 8px 16px; background: ${this.currentMode === "exploded" ? "#0284C7" : "#1E293B"}; color: #FFF; border: 1px solid #334155; border-radius: 8px; font-weight: 700; cursor: pointer;">Explodido</button>
      <button type="button" data-action="mode-step" style="min-height: 48px; min-width: 48px; padding: 8px 16px; background: ${this.currentMode === "step-by-step" ? "#0284C7" : "#1E293B"}; color: #FFF; border: 1px solid #334155; border-radius: 8px; font-weight: 700; cursor: pointer;">Passo a Passo (1 a 10)</button>
    </div>
    <div style="font-family: monospace; font-size: 13px; color: #00F2FE;">Passo ${step.stepNumber} de ${this.getTotalSteps()}</div>
  </div>

  <div style="background: #111827; border: 1px solid #1E3A8A; border-radius: 8px; padding: 12px; margin-bottom: 12px;">
    <div style="display: flex; justify-content: space-between; align-items: center;">
      <h4 style="margin: 0; color: #F8FAFC; font-size: 15px;">[${step.stepNumber}] ${step.name}</h4>
      <span style="font-family: monospace; font-size: 12px; color: #FDE047;">${step.partNumber}</span>
    </div>
    <div style="margin-top: 6px; font-size: 12.5px; color: #CBD5E1;">
      <strong>Torque / Encaixe:</strong> <span style="color: #38BDF8;">${step.torqueSpec || "Conforme montagem"}</span> | 
      <strong>Ferramenta:</strong> <span style="color: #6EE7B7;">${step.requiredTool || "Manual"}</span>
    </div>
    <div style="margin-top: 6px; font-size: 12px; color: #FCA5A5; background: #1C0A0E; padding: 6px 10px; border-radius: 4px; border-left: 3px solid #EF4444;">
      <strong>Gotcha de Oficina:</strong> ${step.criticalGotcha}
    </div>
  </div>

  <div style="display: flex; gap: 8px; justify-content: space-between;">
    <button type="button" data-action="prev-step" style="min-height: 48px; min-width: 120px; background: #1E293B; color: #FFF; border: 1px solid #475569; border-radius: 8px; font-weight: 700; cursor: pointer;" ${this.currentStepIndex === 0 ? "disabled" : ""}>◀ Anterior</button>
    <button type="button" data-action="next-step" style="min-height: 48px; min-width: 120px; background: #0284C7; color: #FFF; border: 1px solid #38BDF8; border-radius: 8px; font-weight: 700; cursor: pointer;" ${this.currentStepIndex === this.getTotalSteps() - 1 ? "disabled" : ""}>Próximo ▶</button>
  </div>
</div>
`.trim();
  }
}
